import React from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, useTheme } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';

/**
 * Shared primary / secondary / text buttons.
 * Colors and typography come from the MUI theme (content-mui/theme/theme.js).
 */
const AppButton = ({
  children,
  variantType = 'primary',
  loading = false,
  disabled = false,
  sx = {},
  ...props
}) => {
  const theme = useTheme();
  const { palette, typography, shape } = theme;

  const muiVariant =
    variantType === 'primary'
      ? 'contained'
      : variantType === 'secondary'
        ? 'outlined'
        : 'text';

  const variantSx = (() => {
    const primaryDark = palette.primary.dark;
    const primaryMain = palette.primary.main;

    switch (variantType) {
      case 'primary':
        return {
          backgroundColor: primaryDark,
          color: palette.primary.contrastText,
          '&:hover': {
            backgroundColor: darken(primaryDark, 0.12),
          },
        };
      case 'secondary':
        return {
          backgroundColor: 'transparent',
          color: primaryDark,
          border: `1px solid ${palette.divider}`,
          '&:hover': {
            borderColor: primaryDark,
            backgroundColor: alpha(primaryMain, 0.08),
          },
        };
      case 'text':
        return {
          backgroundColor: 'transparent',
          color: primaryDark,
          '&:hover': {
            backgroundColor: alpha(primaryMain, 0.08),
          },
        };
      default:
        return {};
    }
  })();

  return (
    <Button
      variant={muiVariant}
      disabled={disabled || loading}
      sx={{
        textTransform: 'none',
        borderRadius: shape.borderRadius,
        px: 3,
        py: 1.2,
        fontWeight: typography.h6.fontWeight,
        fontSize: typography.button.fontSize,
        minWidth: 120,
        ...variantSx,
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={18} sx={{ color: 'inherit' }} />
      ) : (
        children
      )}
    </Button>
  );
};

AppButton.propTypes = {
  children: PropTypes.node.isRequired,
  variantType: PropTypes.oneOf(['primary', 'secondary', 'text']),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default AppButton;
